import { DefaultCachingNumberFormatter as NumberFormatter } from '@square/dashboard-translations/utils/field-kit/default-caching-number-formatter';
import * as currencyFormats from '@square/dashboard-translations/utils/formats/currency';
import DefaultCurrencyCentFormatter from '@square/dashboard-translations/utils/field-kit/default-currency-cent-formatter';
import DecimalEuroCurrencyCentFormatter from '@square/dashboard-translations/utils/field-kit/decimal-euro-currency-cent-formatter';
import GbpCurrencyCentFormatter from '@square/dashboard-translations/utils/field-kit/gbp-currency-cent-formatter';
import JapaneseCurrencyCentFormatter from '@square/dashboard-translations/utils/field-kit/japanese-currency-cent-formatter';
import SatoshiCurrencyFormatter from '@square/dashboard-translations/utils/field-kit/satoshi-currency-formatter';
import BitcoinCurrencyFormatter from '@square/dashboard-translations/utils/field-kit/bitcoin-currency-formatter';
import DefaultPhoneFormatter from '@square/dashboard-translations/utils/field-kit/default-phone-formatter';
import USPhoneFormatter from '@square/dashboard-translations/utils/field-kit/us-phone-formatter';
import AustralianPhoneFormatter from '@square/dashboard-translations/utils/field-kit/australian-phone-formatter';
import CanadianPhoneFormatter from '@square/dashboard-translations/utils/field-kit/canadian-phone-formatter';
import JapanesePhoneFormatter from '@square/dashboard-translations/utils/field-kit/japanese-phone-formatter';
import BritishPhoneFormatter from '@square/dashboard-translations/utils/field-kit/british-phone-formatter';
import { assert } from '@ember/debug';
import Service from '@ember/service';
import { convertFromFullwidth } from '@square/dashboard-translations/utils/halfwidth-fullwidth-converter';
import { isNone } from '@ember/utils';
import { CurrencyCode } from '@square/dashboard-protos/models/squareup/common/currency_codes';
function allowingFullwidth(formatter) {
  const originalParse = formatter.parse;
  formatter.parse = function (string, error) {
    return originalParse.call(this, convertFromFullwidth(string), error);
  };
  return formatter;
}
function getCurrencyCodeFromProto(currencyCodeProto) {
  const currencyCode = CurrencyCode[currencyCodeProto];
  assert(`currencyCode for value '${currencyCodeProto}' must exist`, currencyCode);
  return currencyCode;
}
function getLanguage(locale) {
  if ('Locale' in Intl) {
    return new Intl.Locale(locale).language;
  } else {
    // Fallback for browsers that don't support Intl.Locale, like Safari <= 13.
    return locale.split('-')[0];
  }
}
function getLocaleRegion(locale) {
  if ('Locale' in Intl) {
    return new Intl.Locale(locale).region;
  } else {
    // Fallback for browsers that don't support Intl.Locale, like Safari <= 13.
    return locale.split('-')[1];
  }
}
function shouldUseSpainFormatting(locale, countryCode) {
  if (!locale) {
    return false;
  }
  const language = getLanguage(locale);
  const region = getLocaleRegion(locale);
  if (!['ca', 'es'].includes(language)) {
    return false;
  }
  if (region) {
    return region === 'ES';
  }
  return countryCode === 'ES';
}
function configureSpainNumberFormatter(formatter) {
  formatter.setDecimalSeparator(',');
  formatter.setGroupingSeparator(' ');
  formatter.setPercentSymbol(' %');

  // Override format method to remove grouping separator for 4-digit numbers
  const originalFormatMethod = formatter.format;
  const currencySymbol = formatter.currencySymbol();
  const fourDigitNumberWithSpace = new RegExp(
  // matches '1 234,56 €' and '1 234 €'
  `^\\d\\s\\d{3}(,\\d*)*(\\s${currencySymbol})*$`);
  formatter.format = function (number) {
    const formattedNumber = originalFormatMethod.call(formatter, number);
    if (fourDigitNumberWithSpace.test(formattedNumber)) {
      // Remove the first non-breaking space, leaving the space before the currency symbol
      return formattedNumber.replace(' ', '');
    }
    return formattedNumber;
  };
}
function configureSpainCurrencyFormatter(formatter) {
  configureSpainNumberFormatter(formatter);
  formatter.setPositivePrefix('');
  formatter.setPositiveSuffix(' ¤');
  formatter.setNegativePrefix('-');
  formatter.setNegativeSuffix(' ¤');
}

// eslint-disable-next-line ember/no-classic-classes
export default Service.extend({
  _locale: null,
  _countryCode: null,
  _currencyCode: null,
  _currencyFormatters: null,
  setup(locale, countryCode, currencyCode) {
    this._locale = locale;
    this._countryCode = countryCode;
    this._currencyCode = currencyCode;
  },
  count() {
    throw new Error('count(number) is unimplemented, use FieldKit to implement it');
  },
  number() {
    throw new Error('number(number, precision) is unimplemented, use FieldKit to implement it');
  },
  datetime() {
    throw new Error('datetime formatter has moved to i18n/datetime');
  },
  getNumberFormatter() {
    return this.buildNumberFormatter();
  },
  buildNumberFormatter() {
    const formatter = new NumberFormatter().setLocale(this._locale).setNumberStyle(NumberFormatter.Style.NONE).setMinimumIntegerDigits(1).setMaximumFractionDigits(0).setMinimumFractionDigits(0).setLenient(true);
    if (shouldUseSpainFormatting(this._locale, this._countryCode)) {
      configureSpainNumberFormatter(formatter);
    }
    return allowingFullwidth(formatter);
  },
  buildFractionNumberFormatter() {
    const formatter = new NumberFormatter().setLocale(this._locale).setNumberStyle(NumberFormatter.Style.NONE).setMinimumIntegerDigits(0).setMinimumFractionDigits(0).setLenient(true);
    if (shouldUseSpainFormatting(this._locale, this._countryCode)) {
      configureSpainNumberFormatter(formatter);
    }
    const decimalSeparator = formatter.decimalSeparator();
    function isNumeric(str) {
      // Starting with optional minus sign, optional digits, an optional separator, and optional digits to follow
      return new RegExp(`^\\-?\\d*\\${decimalSeparator}?\\d*$`).test(str);
    }
    formatter.isChangeValid = function (change) {
      const {
        text
      } = change.proposed;
      const convertedText = convertFromFullwidth(text);
      const parts = convertedText.split(decimalSeparator);
      return parts[0].length <= formatter.maximumIntegerDigits() && (parts[1] || '').length <= formatter.maximumFractionDigits() && isNumeric(convertedText);
    };
    formatter.parse = function (value) {
      if (!value) {
        return value;
      }
      const valueWithDecimal = value.replace(decimalSeparator, '.');
      const parsed = Number(valueWithDecimal);
      // eslint-disable-next-line unicorn/prefer-number-properties -- TODO: try enabling this separately
      if (isNaN(parsed)) {
        return null;
      }
      return parsed;
    };
    return allowingFullwidth(formatter);
  },
  getDecimalSeparator() {
    const formatter = new NumberFormatter().setLocale(this._locale);
    if (shouldUseSpainFormatting(this._locale, this._countryCode)) {
      configureSpainNumberFormatter(formatter);
    }
    return formatter.decimalSeparator();
  },
  getGroupedNumberFormatter() {
    return this.buildGroupedNumberFormatter();
  },
  groupedNumber(number) {
    return this.getGroupedNumberFormatter().format(number);
  },
  buildGroupedNumberFormatter({
    maximumFractionDigits = 0,
    minimumFractionDigits = 0
  } = {}) {
    const formatter = new NumberFormatter().setLocale(this._locale).setNumberStyle(NumberFormatter.Style.NONE).setMinimumIntegerDigits(1).setMaximumFractionDigits(maximumFractionDigits).setMinimumFractionDigits(minimumFractionDigits).setUsesGroupingSeparator(true).setLenient(true);
    if (shouldUseSpainFormatting(this._locale, this._countryCode)) {
      configureSpainNumberFormatter(formatter);
    }
    return allowingFullwidth(formatter);
  },
  getGroupedDecimalNumberFormatter(scale = 2) {
    this._groupedDecimalNumberFormatters = this._groupedDecimalNumberFormatters || {};
    if (isNone(this._groupedDecimalNumberFormatters[scale])) {
      this._groupedDecimalNumberFormatters[scale] = this.buildGroupedDecimalNumberFormatter(scale);
    }
    return this._groupedDecimalNumberFormatters[scale];
  },
  getGroupedPreciseNumberFormatter(scale = 2) {
    this._groupedPreciseNumberFormatter = this._groupedPreciseNumberFormatter || {};
    if (isNone(this._groupedPreciseNumberFormatter[scale])) {
      const formatter = this.buildGroupedDecimalNumberFormatter(scale);
      formatter.groupedDecimalNumberFormat = formatter.format;
      formatter.format = function (value) {
        value = this.groupedDecimalNumberFormat(value);
        if (scale > 0) {
          const parts = value.split(formatter.decimalSeparator());
          if (parts.length === 1) {
            parts[1] = '';
          }
          while (parts[1].length < scale) {
            parts[1] += '0';
          }
          value = parts.join(formatter.decimalSeparator());
        }
        return value;
      };
      this._groupedPreciseNumberFormatter[scale] = formatter;
    }
    return this._groupedPreciseNumberFormatter[scale];
  },
  buildGroupedDecimalNumberFormatter(scale) {
    const formatter = new NumberFormatter().setLocale(this._locale).setNumberStyle(NumberFormatter.Style.NONE).setMinimumIntegerDigits(1).setMaximumFractionDigits(scale).setMinimumFractionDigits(0).setUsesGroupingSeparator(true).setLenient(true);
    if (shouldUseSpainFormatting(this._locale, this._countryCode)) {
      configureSpainNumberFormatter(formatter);
    }
    return allowingFullwidth(formatter);
  },
  getHoursFormatter() {
    return this.buildHoursFormatter();
  },
  buildHoursFormatter() {
    return {
      format(value) {
        if (value === null || value === 0 || convertFromFullwidth(value) === '0') {
          return '';
        }
        return value;
      },
      parse(value) {
        if (value === '') {
          return null;
        }
        return Number.parseFloat(convertFromFullwidth(value));
      },
      isChangeValid(change) {
        return /^\d{0,3}(\.\d{0,2})?$/.test(convertFromFullwidth(change.proposed.text));
      }
    };
  },
  getCurrencyFormatter(currencyCode, hideCurrencySymbol) {
    this._currencyFormatters = this._currencyFormatters || {};
    if (isNone(this._currencyFormatters[currencyCode])) {
      this._currencyFormatters[currencyCode] = this.buildCurrencyFormatter(currencyCode);
    }
    if (hideCurrencySymbol) {
      this._currencyFormatters[currencyCode].setCurrencySymbol('');
    } else {
      this._currencyFormatters[currencyCode].setCurrencySymbol();
    }
    return this._currencyFormatters[currencyCode];
  },
  buildCurrencyFormatter(currencyCode) {
    const currencyFormat = currencyFormats[currencyCode] || currencyFormats.standard;
    let formatter;
    if (currencyCode === 'BTC') {
      formatter = new BitcoinCurrencyFormatter();
    } else {
      formatter = new NumberFormatter();
    }
    formatter.setLocale(this._locale).setCountryCode(this._countryCode).setCurrencyCode(currencyCode).setNumberStyle(NumberFormatter.Style.CURRENCY).setExponent(currencyFormat.exponent).setLenient(true);
    if (shouldUseSpainFormatting(this._locale, this._countryCode)) {
      configureSpainCurrencyFormatter(formatter);
    }
    return allowingFullwidth(formatter);
  },
  currency(cents, currencyCode = this._currencyCode, opt) {
    let formatter;
    if (opt) {
      formatter = this.buildCurrencyFormatter(currencyCode);
      formatter.setMaximumFractionDigits(typeof opt.maximumFractionDigits !== 'undefined' ? opt.maximumFractionDigits : formatter.maximumFractionDigits()).setMinimumFractionDigits(typeof opt.minimumFractionDigits !== 'undefined' ? opt.minimumFractionDigits : formatter.minimumFractionDigits());
      if (typeof opt.useMinusSignForNegativeCurrency !== 'undefined' && opt.useMinusSignForNegativeCurrency) {
        formatter.setNegativePrefix('-¤');
        formatter.setNegativeSuffix('');
      }
    } else {
      formatter = this.getCurrencyFormatter(currencyCode);
    }
    return formatter.format(cents);
  },
  formatMoney({
    amount,
    currencyCode
  }, options) {
    return this.currency(amount, typeof currencyCode === 'string' ? currencyCode : getCurrencyCodeFromProto(currencyCode), options);
  },
  formatMoneyRounded(money) {
    return this.formatMoney(money, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    });
  },
  formatMoneyShort(money) {
    if (!money.amount || money.amount % 100 === 0) {
      return this.formatMoneyRounded(money);
    }
    return this.formatMoney(money);
  },
  formatMoneyUsingMinusSign(money) {
    return this.formatMoney(money, {
      useMinusSignForNegativeCurrency: true
    });
  },
  // TODO(ghassan): i18n. Maybe even build this into field-kit
  getCurrencyCentFormatter(currencyCode = this._currencyCode) {
    const useDecimalEuros = this._countryCode === 'FR' || this._countryCode === 'ES';
    return allowingFullwidth((() => {
      switch (currencyCode) {
        case 'JPY':
          return new JapaneseCurrencyCentFormatter();
        case 'GBP':
          return new GbpCurrencyCentFormatter();
        case 'EUR':
          return useDecimalEuros ? new DecimalEuroCurrencyCentFormatter() : new DefaultCurrencyCentFormatter();
        case 'BTC':
          return new SatoshiCurrencyFormatter();
        default:
          return new DefaultCurrencyCentFormatter();
      }
    })());
  },
  currencyCent(cents, currencyCode = this._currencyCode) {
    return this.getCurrencyCentFormatter(currencyCode).format(cents);
  },
  getPercentFormatter() {
    return this.buildPercentFormatter();
  },
  buildPercentFormatter() {
    const formatter = new NumberFormatter().setLocale(this._locale).setNumberStyle(NumberFormatter.Style.PERCENT).setExponent(0) // format .5 as 0.5%, not 50%
    .setLenient(true);
    if (shouldUseSpainFormatting(this._locale, this._countryCode)) {
      configureSpainNumberFormatter(formatter);
    }
    return allowingFullwidth(formatter);
  },
  percent(percent, opt) {
    let formatter;
    if (opt) {
      formatter = this.buildPercentFormatter();
      formatter.setMaximumFractionDigits(typeof opt.maximumFractionDigits !== 'undefined' ? opt.maximumFractionDigits : formatter.maximumFractionDigits()).setMinimumFractionDigits(typeof opt.minimumFractionDigits !== 'undefined' ? opt.minimumFractionDigits : formatter.minimumFractionDigits()).setPositivePrefix(typeof opt.positivePrefix !== 'undefined' ? opt.positivePrefix : formatter.positivePrefix()).setExponent(typeof opt.exponent !== 'undefined' ? opt.exponent : formatter.exponent());
    } else {
      formatter = this.getPercentFormatter();
    }
    return formatter.format(percent);
  },
  processingFee(discountBasisPoints, interchangeCents, currencyCode) {
    const percent = this.percent(discountBasisPoints, {
      exponent: -2,
      maximumFractionDigits: 2
    });
    if (interchangeCents === 0) {
      return percent;
    } else {
      const cents = this.currencyCent(interchangeCents, currencyCode);
      return `${percent} + ${cents}`;
    }
  },
  getBasisPointFormatter() {
    return this.buildBasisPointFormatter();
  },
  buildBasisPointFormatter() {
    const formatter = new NumberFormatter().setLocale(this._locale).setNumberStyle(NumberFormatter.Style.PERCENT).setExponent(-2) // format 275 as 2.75%
    .setMinimumFractionDigits(2).setMaximumFractionDigits(2).setLenient(true);
    if (shouldUseSpainFormatting(this._locale, this._countryCode)) {
      configureSpainNumberFormatter(formatter);
    }
    return allowingFullwidth(formatter);
  },
  basisPoints(basisPoints, opt) {
    let formatter;
    if (opt) {
      formatter = this.buildBasisPointFormatter();
      formatter.setMaximumFractionDigits(typeof opt.maximumFractionDigits !== 'undefined' ? opt.maximumFractionDigits : formatter.maximumFractionDigits()).setMinimumFractionDigits(typeof opt.minimumFractionDigits !== 'undefined' ? opt.minimumFractionDigits : formatter.minimumFractionDigits()).setExponent(typeof opt.exponent !== 'undefined' ? opt.exponent : formatter.exponent());
    } else {
      formatter = this.getBasisPointFormatter();
    }
    return formatter.format(basisPoints);
  },
  getPhoneNumberFormatter() {
    return allowingFullwidth((() => {
      switch (this._countryCode) {
        case 'AU':
          return new AustralianPhoneFormatter();
        case 'CA':
          return new CanadianPhoneFormatter();
        case 'JP':
          return new JapanesePhoneFormatter();
        case 'US':
          return new USPhoneFormatter();
        case 'GB':
          return new BritishPhoneFormatter();
        default:
          return new DefaultPhoneFormatter(this._countryCode);
      }
    })());
  },
  phoneNumber(phoneNumber) {
    // TODO: COMDASH-3122 - deprecate phone number formatter from service in favor of libphonenumber-js
    return this.getPhoneNumberFormatter().format(phoneNumber);
  },
  buildSupportCustomerCodeFormatter() {
    const formatter = new NumberFormatter().setUsesGroupingSeparator(true).setGroupingSeparator('-').setGroupingSize(4);
    return allowingFullwidth(formatter);
  },
  supportCustomerCode(customerCode) {
    return this.buildSupportCustomerCodeFormatter().format(customerCode);
  }
});